"use client";

import { Button, type ImageProps } from "@pamperedchef/design-system";
import type { HeadingTag } from "@/app/utilities/types";
import { debounce } from "@/app/utilities/debounce";
import { resolveImageProps } from "@/app/utilities/images";
import NextImage, { type ImageProps as NextImageProps } from "next/image";
import { type LinkProps as NextLinkProps } from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState, useTransition } from "react";

import {
	connectConsultant,
	searchConsultantByLocationServer,
	searchConsultantByNameServer,
	selectConsultant
} from "@/app/actions/consultant";
import {
	fetchActiveConsultantParty,
	getPartyUrlAction,
	searchPartyByNameServer
} from "@/app/actions/party";
import { FindPartyAndConsultant } from "@/composite/FindPartyAndConsultant";
import { SessionState, useSessionStore } from "@/app/store";
import {
	ConsultantItemReference,
	FindPartyAndConsultantPageTemplate,
	PartyItemReference
} from "@/types";

import { ConsultantConsultant } from "../contact-consultant/client";

type ConsultantSearchType = "name" | "location";

const LOCATION_FILTER = "location";
const NAME_FILTER = "name";

interface ConsultantItem extends Omit<
	ConsultantItemReference,
	"consultantImage"
> {
	consultantImage?: ImageProps<NextImageProps>;
}

/**
 * FindPartyAndConsultant block component
 */
export function FindPartyAndConsultantBlock(
	props: FindPartyAndConsultantPageTemplate
): JSX.Element {
	const [consultantSearchValue, setConsultantSearchValue] = useState("");
	const [consultantSearchFilter, setConsultantSearchFilter] =
		useState<ConsultantSearchType>(NAME_FILTER);
	const [consultantSuggestions, setConsultantSuggestions] = useState<
		ConsultantItem[]
	>([]);
	const [partiesSearchValue, setPartiesSearchValue] = useState("");
	const [partiesSuggestions, setPartiesSuggestions] = useState<
		PartyItemReference[]
	>([]);
	const [isPending, startTransition] = useTransition();
	const [selectedConsultant, setSelectedConsultant] =
		useState<ConsultantItemReference | null>(null);
	const [selectedParty, setSelectedParty] =
		useState<PartyItemReference | null>(null);
	const [partiesList, setPartiesList] = useState<PartyItemReference[]>([]);
	const [isContactModalOpen, setIsContactModalOpen] = useState(false);
	const [isItemSelected, setIsItemSelected] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [, setError] = useState<string | null>(null);
	const modalTriggerRef = useRef(null);
	const router = useRouter();

	const isConsultantNameFilter = consultantSearchFilter === NAME_FILTER;
	const isConsultantActivePWS = selectedConsultant?.activePWS;
	const sessionState = useSessionStore(
		(state) => state.sessionState
	) as SessionState;

	useEffect(() => {
		const activeConsultantPartyAction = async () => {
			try {
				const suggestions =
					await fetchActiveConsultantParty(sessionState);

				setPartiesSuggestions(suggestions);
			} catch (error) {
				console.error("client:active-consultant-party", error);
			}
		};

		activeConsultantPartyAction();
	}, [sessionState]);

	/**
	 * Debounced consultant search function
	 */
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedSearchConsultant = useCallback(
		debounce(
			async (
				value: string,
				type: ConsultantSearchType,
				sessionState: SessionState
			) => {
				if (!value) {
					setConsultantSuggestions([]);

					return;
				}

				startTransition(async () => {
					try {
						// setError(null);
						let data: ConsultantItemReference[] = [];

						if (type === NAME_FILTER) {
							data = await searchConsultantByNameServer(
								value,
								sessionState
							);
						}

						if (type === LOCATION_FILTER) {
							data = await searchConsultantByLocationServer(
								value,
								sessionState
							);
						}

						const newSuggestions: ConsultantItem[] = data?.map(
							(item) => ({
								activePWS: item?.activePWS,
								consultantId: item?.consultantId,
								consultantPk: item?.consultantPk,
								consultantName: item?.consultantName,
								consultantImage: item?.imageUrl
									? {
											...resolveImageProps(),
											component: NextImage,
											src: `https://www.pamperedchef.com${item?.imageUrl}`,
											alt: item?.consultantName ?? "",
											height: 36,
											width: 36
										}
									: undefined,
								location: item?.location
							})
						);

						setConsultantSuggestions(newSuggestions);
					} catch (err) {
						// setError(t("consultantsFetchError"));
						setConsultantSuggestions([]);
					}
				});
			},
			300
		),
		[]
	);

	/**
	 * Handle input change for consultant search
	 */
	function handleConsultantInputChange(
		value: string,
		type: ConsultantSearchType,
		name?: string
	): void {
		setConsultantSearchValue(name ? name : value);
		debouncedSearchConsultant(value, type, sessionState);
	}

	/**
	 * Debounced parties search function
	 */
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedSearchParty = useCallback(
		debounce(async (value: string) => {
			if (!value) {
				setPartiesSuggestions([]);

				return;
			}

			startTransition(async () => {
				try {
					// setError(null);
					const data = await searchPartyByNameServer(value);

					const newSuggestions = data?.map((item) => ({
						showPk: item?.showPk,
						date: item?.showDate_M_D_Y || "",
						hostName: item?.hostName,
						location: item?.location
					}));

					setPartiesSuggestions(newSuggestions);
				} catch (err) {
					// setError(t("partiesFetchError"));
					setPartiesSuggestions([]);
				}
			});
		}, 300),
		[]
	);

	/**
	 * Handle input change for party search
	 */
	function handlePartyInputChange(value: string): void {
		setPartiesSearchValue(value);
		debouncedSearchParty(value);
	}

	/**
	 * Select a consultant from search results
	 */
	async function handleSelectConsultant(consultantId: string): Promise<void> {
		try {
			// Find the consultant from the existing suggestions instead of making an API call
			const consultantItem = consultantSuggestions.find(
				(c) => c.consultantId === consultantId
			);

			if (!consultantItem) {
				console.error(
					`Consultant with ID ${consultantId} not found in suggestions`
				);
				return;
			}

			const activePWS = consultantItem?.activePWS;
			let pwsUrl = undefined;

			if (activePWS) {
				pwsUrl = await connectConsultant(
					sessionState,
					consultantItem.consultantId
				);
			}

			if (!activePWS) {
				setSelectedConsultant(consultantItem);
				// Still need to fetch parties list for consultants without PWS
				const { partiesList: ptList } = await selectConsultant(
					sessionState,
					consultantId
				);
				setPartiesList(ptList as PartyItemReference[]);
			} else if (activePWS && pwsUrl) {
				window.location.href = `${window.location.origin}/pws/${pwsUrl}`;
			} else {
				setSelectedConsultant(null);
				setPartiesList([]);
			}
		} catch (err) {
			console.error(`failed to fetch consultant and party : ${err}`);
			setSelectedConsultant(null);
			setPartiesList([]);
		}
	}

	/**
	 * Select a list from search results
	 */
	async function handleSelectParty(showPk: string): Promise<void> {
		try {
			setIsLoading(true);
			const partyUrl = await getPartyUrlAction(showPk as string);

			window.location.href = `/party/${partyUrl}`;
			setIsLoading(false);
		} catch (_) {
			setIsLoading(false);
			setSelectedParty(null);
			setSelectedConsultant(null);
		}
	}

	/**
	 * Select a list from  parties list
	 */
	async function handleSelectPartyListItem(
		showPk: string | null
	): Promise<void> {
		try {
			setIsLoading(true);
			const partyUrl = await getPartyUrlAction(showPk as string);

			router.push(`/party/${partyUrl}`);
			setIsItemSelected(false); //TODO: Replace with true after service implemented
		} catch (_) {
			setIsLoading(false);
			setSelectedParty(null);
		}
	}

	/**
	 * Get user location using browser
	 */
	async function handleLocationSearch(): Promise<void> {
		if (!navigator.geolocation) {
			setError("Geolocation is not supported by your browser");

			return;
		}

		setIsLoading(true);
		setError(null);

		navigator.geolocation.getCurrentPosition(
			async (position) => {
				const { latitude, longitude } = position.coords;

				setConsultantSearchValue(`${latitude},${longitude}`);

				try {
					const data = await searchConsultantByLocationServer(
						`${latitude},${longitude}`,
						sessionState
					);

					const newSuggestions = data?.map((item) => ({
						activePWS: item?.activePWS,
						consultantId: item?.consultantId,
						consultantPk: item?.consultantPk,
						consultantName: item?.consultantName,
						consultantImage: item?.imageUrl
							? {
									...resolveImageProps(),
									component: NextImage,
									src: `https://www.pamperedchef.com${item?.imageUrl}`,
									alt: item?.consultantName ?? "",
									height: 36,
									width: 36
								}
							: undefined,
						location: item?.location
					}));

					setConsultantSuggestions(newSuggestions);
				} catch (err) {
					setError("Failed to fetch consultants or address");
				} finally {
					setIsLoading(false);
				}
			},
			() => {
				setError("Unable to retrieve location");
				setIsLoading(false);
			}
		);
	}

	let headline =
		props.find_a_party_confirmation?.connected_party_confirmation_headline?.headline?.replace(
			"[ConsultantName]",
			selectedParty?.hostName || ""
		);
	let headlineTag =
		props.find_a_party_confirmation?.connected_party_confirmation_headline
			?.headline_tag;
	let connectedInstruction =
		props.find_a_party_confirmation?.party_connected_consultant_section
			?.additional_description;
	if (selectedConsultant && !selectedParty) {
		headline =
			props.find_a_consultant_confirmation
				?.connected_consultant_confirmation_headline?.headline;
		headlineTag =
			props.find_a_consultant_confirmation
				?.connected_consultant_confirmation_headline?.headline_tag;
		connectedInstruction =
			props.find_a_consultant_confirmation
				?.consultant_connected_description;
	}

	if (!isConsultantActivePWS) {
		headline =
			props.find_a_consultant_confirmation?.inactive_consultant
				?.inactive_consultant_section_title?.headline;
		headlineTag =
			props.find_a_consultant_confirmation?.inactive_consultant
				?.inactive_consultant_section_title?.headline_tag;
	}

	return (
		<FindPartyAndConsultant<NextLinkProps, NextImageProps>
			headline={
				isItemSelected
					? props.find_a_consultant_confirmation
							?.connected_consultant_confirmation_headline
							?.headline
					: headline
			}
			headlineTag={headlineTag as HeadingTag}
			partySelectedHeadline={headline}
			partySelectedHeadlineTag={headlineTag as HeadingTag}
			consultantDetails={selectedConsultant as never}
			connectionInstruction={connectedInstruction}
			partyConnectionInstruction={props.find_a_party_confirmation?.party_connected_consultant_section?.consultant_headline?.replace(
				"[ConsultantName]",
				selectedParty?.hostName || ""
			)}
			consultantForm={{
				headline: props.find_a_consultant?.find_a_consultant_title,
				inputLabel: isConsultantNameFilter
					? props.find_a_consultant?.search_by_name?.input_label
					: props.find_a_consultant?.search_by_location_form_title
							?.input_label,
				inputPlaceholder: isConsultantNameFilter
					? props.find_a_consultant?.search_by_name
							?.input_placeholder_text
					: props.find_a_consultant?.search_by_location_form_title
							?.input_placeholder_text,
				suggestions: {
					loading: isPending || isLoading,
					items: consultantSuggestions
				},
				legend: props.find_a_consultant?.help_text,
				filters: [
					{
						"aria-label":
							props.find_a_consultant?.radio_label.by_name
								.aria_label,
						value: NAME_FILTER,
						children:
							props.find_a_consultant?.radio_label.by_name
								?.input_label
					},
					{
						"aria-label":
							props.find_a_consultant?.radio_label.by_location
								.aria_label,
						value: LOCATION_FILTER,
						children:
							props.find_a_consultant?.radio_label.by_location
								?.input_label
					}
				],
				value: consultantSearchValue,
				defaultFilter: NAME_FILTER,
				selectedFilter: consultantSearchFilter,
				onLocationSearch: isConsultantNameFilter
					? undefined
					: handleLocationSearch,
				onSelection: handleSelectConsultant,
				onInputChange: (value, name) => {
					handleConsultantInputChange(
						value,
						isConsultantNameFilter ? NAME_FILTER : LOCATION_FILTER,
						name
					);
				},
				onFilterChange: (value) =>
					setConsultantSearchFilter(value as ConsultantSearchType),
				googleMapsApiKey: props.googleMapsApiKey
			}}
			partyForm={{
				headline: props.find_a_party?.find_a_party_title,
				inputLabel:
					props.find_a_party?.findaparty_input_field?.input_label,
				inputPlaceholder:
					props.find_a_party?.findaparty_input_field
						?.input_placeholder_text,
				suggestions: {
					loading: isPending || isLoading,
					items: partiesSuggestions as never
				},
				value: partiesSearchValue,
				onSelection: handleSelectParty,
				onInputChange: handlePartyInputChange
			}}
			partyDetails={
				selectedParty
					? {
							headline:
								props.find_a_party_confirmation?.party_details
									?.party_details_headline,
							disclaimer:
								props.find_a_party_confirmation?.party_details
									?.party_details_description,
							...selectedParty
						}
					: undefined
			}
			partiesList={{
				headline:
					props.find_a_consultant_confirmation
						?.connect_to_party_section_title?.headline,
				headlineTag: props.find_a_consultant_confirmation
					?.connect_to_party_section_title
					?.headline_tag as HeadingTag,
				items: partiesList,
				cta: {
					children: "Show More"
				},
				hasMoreItems: true, // TODO: implement pagination service
				onListSelect: handleSelectPartyListItem,
				onLoadMore: () => null // TODO: implement pagination service
			}}
			notActiveInfo={
				!isConsultantActivePWS && selectedConsultant && !selectedParty
					? {
							description:
								props.find_a_consultant_confirmation?.inactive_consultant?.description?.replace(
									"[ConsultantName]",
									selectedConsultant?.consultantName || ""
								),
							modal: (
								<ConsultantConsultant
									triggerElement={
										<Button
											ref={modalTriggerRef}
											kind="primary"
											surface="default"
											size="lg"
											onClick={() =>
												setIsContactModalOpen(true)
											}
										>
											{
												props
													?.find_a_consultant_confirmation
													?.inactive_consultant
													?.modal_cta
													?.cta_modal_label_text
											}
										</Button>
									}
									consultant={{
										consultantNumber:
											selectedConsultant?.consultantId ||
											"",
										name:
											selectedConsultant?.consultantName ||
											"",
										location: selectedConsultant?.location
									}}
									thankYouMessage={{
										title: "Thanks for your message",
										description: "Your email has been sent."
									}}
									captchaSiteKey={props.captchaSiteKey}
									openByDefault={isContactModalOpen}
									onClose={() => setIsContactModalOpen(false)}
								/>
							)
						}
					: undefined
			}
		/>
	);
}
